import {
  Briefcase,
  Building2,
  Calendar,
  FileText,
  Home,
  Plus,
  Scale,
  Sparkles,
  Stethoscope,
  Utensils,
  Wand2,
} from "lucide-react";

import { useAgentTemplates } from "@/features/agents/hooks";
import type { AgentTemplate } from "@/features/agents/api";

const VERTICAL_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  healthcare: Stethoscope,
  hospitality: Utensils,
  real_estate: Home,
  legal: Scale,
  retail: Briefcase,
  finance: Briefcase,
  education: FileText,
  home_services: Building2,
  automotive: Briefcase,
  other: Sparkles,
};

const ICON_BY_NAME: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  stethoscope: Stethoscope,
  utensils: Utensils,
  home: Home,
  scale: Scale,
  briefcase: Briefcase,
  "calendar-heart": Calendar,
  sparkles: Sparkles,
};

export function iconForTemplate(tpl: AgentTemplate) {
  if (tpl.icon && ICON_BY_NAME[tpl.icon]) return ICON_BY_NAME[tpl.icon];
  return VERTICAL_ICONS[tpl.vertical] ?? Sparkles;
}

type Props = {
  onPick: (tpl: AgentTemplate | null) => void;
  selectedId?: string | null;
  showBlank?: boolean;
  blankLabel?: string;
  blankHint?: string;
  onCreateNew?: () => void;
};

export default function TemplatePickerCards({
  onPick,
  selectedId,
  showBlank = true,
  blankLabel = "Start from scratch",
  blankHint = "Blank agent — write your own prompt.",
  onCreateNew,
}: Props) {
  const templatesQ = useAgentTemplates();
  const templates = templatesQ.data ?? [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {showBlank && (
          <button
            type="button"
            onClick={() => onPick(null)}
            className={
              "flex w-full items-center gap-3 rounded-lg border bg-card px-4 py-3 text-left transition hover:border-primary hover:bg-accent/40 " +
              (selectedId === null
                ? "border-primary ring-1 ring-primary/30"
                : "")
            }
          >
            <div className="flex size-9 items-center justify-center rounded-md border bg-background">
              <Sparkles className="size-4 text-muted-foreground" />
            </div>
            <div>
              <div className="text-sm font-medium">{blankLabel}</div>
              <div className="text-xs text-muted-foreground">{blankHint}</div>
            </div>
          </button>
        )}
        {onCreateNew && (
          <button
            type="button"
            onClick={onCreateNew}
            className="flex w-full items-center gap-3 rounded-lg border border-dashed bg-card px-4 py-3 text-left transition hover:border-primary hover:bg-accent/40"
          >
            <div className="flex size-9 items-center justify-center rounded-md border bg-background">
              <Wand2 className="size-4 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-1 text-sm font-medium">
                <Plus className="size-3.5" />
                Create your own template
              </div>
              <div className="text-xs text-muted-foreground">
                Describe your business, let AI draft the prompt.
              </div>
            </div>
          </button>
        )}
      </div>

      {templatesQ.isLoading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-lg border bg-muted/30"
            />
          ))}
        </div>
      ) : templates.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No templates configured yet.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {templates.map((tpl) => {
            const Icon = iconForTemplate(tpl);
            const selected = selectedId === tpl.id;
            return (
              <button
                key={tpl.id}
                type="button"
                onClick={() => onPick(tpl)}
                className={
                  "group flex flex-col items-start gap-2 rounded-lg border bg-card p-4 text-left transition hover:border-primary hover:bg-accent/40 " +
                  (selected ? "border-primary ring-1 ring-primary/30" : "")
                }
              >
                <div className="flex w-full items-center gap-3">
                  <div className="flex size-9 items-center justify-center rounded-md border bg-background">
                    <Icon className="size-4 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium">
                        {tpl.name}
                      </span>
                      {!tpl.is_platform && (
                        <span className="inline-flex items-center rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                          Yours
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {tpl.vertical_display}
                    </div>
                  </div>
                </div>
                {tpl.description && (
                  <p className="line-clamp-3 text-xs text-muted-foreground">
                    {tpl.description}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
